import React, { useState, useEffect } from 'react';
import {
    Container, Typography, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Menu, MenuItem, Snackbar, Alert, InputAdornment, Box,
    Select, FormControl, InputLabel, Pagination, CircularProgress, Grid
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import api from '../utils/api';
import { Visibility, VisibilityOff, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ViewParties = () => {
    const [parties, setParties] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedParty, setSelectedParty] = useState(null);
    const [openOrders, setOpenOrders] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [editedParty, setEditedParty] = useState({});
    const [password, setPassword] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const userRole = localStorage.getItem('userRole');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    // Pagination states
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [location, setLocation] = useState('All');
    const [searchInput, setSearchInput] = useState('');
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const fetchParties = async (searchValue = searchTerm, pageNum = page, loc = location) => {
        setLoading(true);
        try {
            const response = await api.get('/parties/paginated', {
                params: { page: pageNum, limit: 20, search: searchValue, location: loc }
            });
            setParties(response.data.parties);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching parties:', error);
            setSnackbar({ open: true, message: 'Failed to fetch parties', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Initial load - only once
    useEffect(() => {
        if (!initialLoadDone) {
            fetchParties('', 1, 'All');
            setInitialLoadDone(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch when page or location changes (but not on initial load)
    useEffect(() => {
        if (initialLoadDone) {
            fetchParties(searchTerm, page, location);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, location]);

    const handleSearch = (event) => {
        setSearchInput(event.target.value);
    };

    const handleSearchSubmit = () => {
        setSearchTerm(searchInput);
        setPage(1);
        // Pass the current input value directly to avoid async state issue
        fetchParties(searchInput, 1, location);
    };

    const handleLocationChange = (event) => {
        const newLocation = event.target.value;
        setLocation(newLocation);
        setPage(1);
        // Fetch with new location directly
        fetchParties(searchTerm, 1, newLocation);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo(0, 0);
    };

    const filteredParties = parties;

    const handlePartyClick = async (event, party) => {
        event.stopPropagation();
        setSelectedParty(party);
        try {
            const response = await api.get(`/orders/pending-by-party/${party._id}`);
            setSelectedParty({ ...party, pendingOrders: response.data });
            setOpenOrders(true);
        } catch (error) {
            console.error('Error fetching pending orders:', error);
            setSnackbar({ open: true, message: 'Failed to fetch pending orders', severity: 'error' });
        }
    };

    const handleMenuOpen = (event, party) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedParty(party);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        setEditedParty({ ...selectedParty });
        setOpenEdit(true);
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setOpenDelete(true);
        handleMenuClose();
    };

    const handleEditChange = (event) => {
        setEditedParty({ ...editedParty, [event.target.name]: event.target.value });
    };

    const handleEditSubmit = async () => {
        try {
            await api.put(`/parties/${selectedParty._id}`, editedParty);
            setSnackbar({ open: true, message: 'Party updated successfully', severity: 'success' });
            fetchParties();
            setOpenEdit(false);
        } catch (error) {
            console.error('Error updating party:', error);
            setSnackbar({ open: true, message: 'Failed to update party', severity: 'error' });
        }
    };


    const handleDeleteSubmit = async () => {
        try {
            await api.delete(`/parties/${selectedParty._id}`, { data: { password } });
            setSnackbar({ open: true, message: 'Party deleted successfully', severity: 'success' });
            fetchParties();
            setOpenDelete(false);
        } catch (error) {
            console.error('Error deleting party:', error);
            const errorMessage = error.response?.data?.errors?.[0]?.msg 
                || error.response?.data?.message 
                || 'Failed to delete Party';

            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
                >
                    View Parties
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Manage customer information and track pending orders for each party
                </Typography>
                {userRole === 'owner' && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => navigate('/add-party')}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                            }
                        }}
                    >
                        Add New Party
                    </Button>
                )}
            </Box>
            {/* Search and Filter Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Search Parties"
                        variant="outlined"
                        value={searchInput}
                        onChange={handleSearch}
                        onFocus={(e) => e.target.select()}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                        placeholder="Search by name, contact, phone, or city..."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearchSubmit} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Filter by Location</InputLabel>
                        <Select
                            value={location}
                            label="Filter by Location"
                            onChange={handleLocationChange}
                        >
                            <MenuItem value="All">All Locations</MenuItem>
                            <MenuItem value="Ladnun">Ladnun</MenuItem>
                            <MenuItem value="Parihara">Parihara</MenuItem>
                            <MenuItem value="Chhapar">Chhapar</MenuItem>
                            <MenuItem value="Sujangarh">Sujangarh</MenuItem>
                            <MenuItem value="Bidasar">Bidasar</MenuItem>
                            <MenuItem value="Salasar">Salasar</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        {loading ? 'Loading...' : `${pagination.totalRecords || 0} parties found`}
                    </Typography>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Firm Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Address</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Contact Person</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Phone Number</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>Loading parties...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredParties.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <Typography color="text.secondary">
                                        No parties found. {searchTerm || location !== 'All' ? 'Try adjusting your filters.' : 'Add a new party to get started.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredParties.map((party) => (
                                <TableRow
                                    key={party._id}
                                    onClick={(e) => handlePartyClick(e, party)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <TableCell>{party.firmName}</TableCell>
                                    <TableCell>{`${party.firmAddress}, ${party.firmCityState}`}</TableCell>
                                    <TableCell>{party.contactPerson}</TableCell>
                                    <TableCell>{party.phoneNumber}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, party)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {pagination.total > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <Pagination
                        count={pagination.total}
                        page={page}
                        onChange={(event, value) => handlePageChange(value)}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                {userRole === 'owner' && (
                    <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                )}
            </Menu>

            {/* Pending Orders Dialog */}
            <Dialog open={openOrders} onClose={() => setOpenOrders(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{
                    fontWeight: 'bold',
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: 2
                }}>
                    Pending Orders for {selectedParty?.firmName}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedParty?.pendingOrders && selectedParty.pendingOrders.length > 0 ? (
                        selectedParty.pendingOrders.map((order, orderIndex) => (
                            <Paper key={order._id} elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Order #{orderIndex + 1}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </Typography>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Quantity</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {order.products.map((product) => (
                                                <TableRow
                                                    key={product._id}
                                                    sx={{
                                                        backgroundColor: product.status === 'shipped' ? '#e8f5e9' : 'inherit',
                                                        '&:hover': { backgroundColor: product.status === 'shipped' ? '#c8e6c9' : '#f5f5f5' }
                                                    }}
                                                >
                                                    <TableCell>
                                                        {product.productName}
                                                        {product.description && (
                                                            <Typography variant="caption" color="primary.main" display="block" sx={{ fontStyle: 'italic' }}>
                                                                {product.description}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{product.quantity}</TableCell>
                                                    <TableCell align="right">₹{product.rate.toFixed(2)}</TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{
                                                            display: 'inline-block',
                                                            px: 2,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            bgcolor: product.status === 'shipped' ? 'success.light' : 'warning.light',
                                                            color: product.status === 'shipped' ? 'success.dark' : 'warning.dark',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.75rem',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {product.status}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Total Items: {order.products.length}
                                    </Typography>
                                </Box>
                            </Paper>
                        ))
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No pending orders for this party
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenOrders(false)} variant="contained" color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Party Dialog */}
            <Dialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}>
                    Edit Party
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Firm Name"
                        name="firmName"
                        value={editedParty.firmName || ''}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        variant="outlined"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Firm Address (Optional)"
                        name="firmAddress"
                        value={editedParty.firmAddress || ''}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        fullWidth
                        label="Firm City/State"
                        name="firmCityState"
                        value={editedParty.firmCityState || ''}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        variant="outlined"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Contact Person (Optional)"
                        name="contactPerson"
                        value={editedParty.contactPerson || ''}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        fullWidth
                        label="Phone Number (Optional)"
                        name="phoneNumber"
                        value={editedParty.phoneNumber || ''}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenEdit(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Party Dialog */}
            <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Delete Party</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this party? This action cannot be undone.</Typography>
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        margin="normal"
                        variant="outlined"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
                    <Button onClick={handleDeleteSubmit} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};


export default ViewParties;